/**
 * Atlas PM · 项目管理 · 接口封装
 *
 * 对接后端 ProjectController（apps/api/src/modules/project/project.controller.ts）：
 *   GET    /projects        列表（分页 + 筛选 + 数据范围）
 *   GET    /projects/:id    详情
 *   POST   /projects        创建（需 project:create）
 *   PATCH  /projects/:id    更新（需 project:update，code 不可改）
 *   DELETE /projects/:id    软删除（需 project:delete，有任务则拒绝）
 *
 * 省份清单来自静态资源 /geo/china.json（与地图同源），
 * 过滤 level === 'province' 得到全部 34 个省级行政区（含港澳台）。
 */

import { api, qs } from '../../api/client';
import type { Paginated } from '../../api/types';
import type { AutoTaskInput } from './autoTaskApi';

/* ── 领域类型（与后端 project.types.ts 对齐） ── */

export interface ProjectListItem {
  id: string;
  code: string;
  name: string;
  shortName: string | null;
  projectType: string;
  /** ⭐ P8.7 运营模式：POS=全运营服务 / DOS=数据运营服务 */
  operatingMode: 'POS' | 'DOS';
  provinceCode: string;
  provinceName: string;
  longitude: number;
  latitude: number;
  status: string;
  healthScore: number;
  progressPct: number;
  overdueTaskCnt: number;
  ownerId: string | null;
  startDate: string | null;
  endDate: string | null;
  isSecret: boolean;
  createdAt: string;
  /** ⭐ P6.54 项目齐套率（%）；无「需附件要素」时为 null（前端显示「—」） */
  deliverableReadyPct: number | null;
  /** 项目下全部「需附件要素」数 */
  deliverableRequiredCnt: number;
  /** 其中已上传至少 1 个有效附件的要素数 */
  deliverableSatisfiedCnt: number;
}

export interface ProjectDetail extends ProjectListItem {
  cityCode: string | null;
  cityName: string | null;
  districtCode: string | null;
  coverageRadiusM: number | null;
  memberIds: string[];
  tags: string[];
  remark: string | null;
  updatedAt: string;
}

/** ⭐ P6.54 项目级齐套率（实时口径） */
export interface ProjectCompletenessInfo {
  projectId: string;
  completeness: number | null;
  completenessPct: number | null;
  requiredTotal: number;
  requiredSatisfied: number;
}

/** 创建 / 更新项目的入参（严格对齐 CreateProjectDto） */
export interface ProjectInput {
  code: string;
  name: string;
  shortName?: string;
  projectType: string;
  /** ⭐ P8.7 运营模式：必填，POS / DOS */
  operatingMode: 'POS' | 'DOS';
  provinceCode: string;
  provinceName?: string;
  /** ⭐ P6.65 地级市 adcode（直辖市 / 港澳台无此层，可为空） */
  cityCode?: string;
  /** ⭐ P6.65 地级市名称 */
  cityName?: string;
  /** ⭐ P6.65 区县 adcode（选填；为空时定位到市中心点） */
  districtCode?: string;
  longitude: number;
  latitude: number;
  startDate?: string;
  endDate?: string;
  remark?: string;
  /** ⭐ P6.27：项目负责人用户 id（空串 = 清空） */
  ownerId?: string;
  /** ⭐ P8.5：项目成员用户 id 列表（空数组 = 清空；负责人不应重复计入） */
  memberIds?: string[];
  /** ⭐ P8.29：建项目时的自动任务配置（选模板 + 当场设周期；仅 create 提交，PATCH 不下发） */
  autoTasks?: AutoTaskInput[];
}

export type ProjectUpdateInput = Partial<ProjectInput> & { status?: string };

export interface ProjectListQuery {
  page?: number;
  pageSize?: number;
  keyword?: string;
  provinceCode?: string;
  status?: string;
  projectType?: string;
}

/* ── 枚举（英文值 + 中文标签；projectType 为自由字符串，这里给出常用档） ── */

export interface Option {
  value: string;
  label: string;
  /** 业务数据色值（来自 /api/project-types）；回退常量可不带 */
  color?: string | null;
}

/**
 * 项目类型·回退常量（P4.8）。
 *
 * ⚠️ 保留此导出名以保证向后兼容。真实列表由 `useProjectTypes()` 从
 *    `/api/project-types` 拉取；接口失败 / 无数据时表单回退到这里，
 *    保证下拉框不空。
 */
export const PROJECT_TYPES: readonly Option[] = [
  { value: 'CHARGING', label: '充电站' },
  { value: 'SOLAR', label: '光伏' },
  { value: 'STORAGE', label: '储能' },
  { value: 'SUBSTATION', label: '变电站' },
  { value: 'MICROGRID', label: '微电网' },
] as const;

export const PROJECT_STATUSES: readonly Option[] = [
  { value: 'PREPARING', label: '筹备中' },
  { value: 'ACTIVE', label: '进行中' },
  { value: 'PAUSED', label: '已暂停' },
  { value: 'CLOSING', label: '收尾中' },
  { value: 'CLOSED', label: '已关闭' },
] as const;

/**
 * 运行时项目类型缓存（兼容层）。
 *
 * `useProjectTypes()` 拉取成功后写入这里，失败 / 加载中写 null。
 * 这样纯函数 `projectTypeLabel()`（如地图筛选项使用）无需改动签名，
 * 也能在数据可用时反映管理员改过的名称；数据不可用时自动回退常量。
 */
let runtimeProjectTypes: readonly Option[] | null = null;

/** 写入 / 清空运行时项目类型（供 useProjectTypes 调用） */
export function setRuntimeProjectTypes(list: readonly Option[] | null): void {
  runtimeProjectTypes = list && list.length > 0 ? list : null;
}

/** 当前生效的项目类型（运行时优先，否则回退常量） */
export function effectiveProjectTypes(): readonly Option[] {
  return runtimeProjectTypes ?? PROJECT_TYPES;
}

export function projectTypeLabel(value: string): string {
  return effectiveProjectTypes().find((t) => t.value === value)?.label ?? value;
}

/** 项目类型对应的业务色值（无则返回 null） */
export function projectTypeColor(value: string): string | null {
  return effectiveProjectTypes().find((t) => t.value === value)?.color ?? null;
}

export function projectStatusLabel(value: string): string {
  return PROJECT_STATUSES.find((s) => s.value === value)?.label ?? value;
}

/* ── 项目类型（公开只读） ── */

/**
 * 启用中的项目类型（对齐后端 PublicProjectTypeController 返回）。
 *
 * 后端字段为 `code` / `name`，前端统一 `Option` 的 `value` / `label`，
 * 映射在 `useProjectTypes()` 内完成。
 */
export interface ActiveProjectType {
  id: string;
  code: string;
  name: string;
  color: string | null;
  sortOrder: number;
  isActive: boolean;
}

/**
 * 公开只读接口：登录即可（无需 dict:manage），只返回 isActive=true 的项。
 * client 会补 `/api` 前缀 → 实际请求 `/api/project-types`。
 */
export const activeProjectTypeApi = {
  list: () => api.get<ActiveProjectType[]>('/project-types'),
};

/* ── 接口方法 ── */

export const projectApi = {
  list: (query: ProjectListQuery) =>
    api.get<Paginated<ProjectListItem>>(`/projects${qs(query as Record<string, unknown>)}`),

  detail: (id: string) => api.get<ProjectDetail>(`/projects/${id}`),

  /** ⭐ P6.54 项目级齐套率（实时口径，与任务级一致） */
  completeness: (id: string) =>
    api.get<ProjectCompletenessInfo>(`/projects/${id}/completeness`),

  create: (input: ProjectInput) => api.post<ProjectDetail>('/projects', input),

  update: (id: string, input: ProjectUpdateInput) =>
    api.patch<ProjectDetail>(`/projects/${id}`, input),

  remove: (id: string) => api.delete<{ ok: boolean; id: string }>(`/projects/${id}`),
};

/* ── 省份清单 ── */

export interface ProvinceOption {
  code: string;
  name: string;
  /** [经度, 纬度]，用于选省后自动带出地图位置 */
  center: [number, number] | null;
}

interface ChinaGeoFeature {
  properties: {
    adcode: number | string;
    name?: string;
    level?: string;
    center?: [number, number];
    centroid?: [number, number];
  };
}

let provincesPromise: Promise<ProvinceOption[]> | null = null;

/**
 * 读取全部 34 个省级行政区（含港澳台）。
 * 结果在模块内缓存，避免表单 / 筛选重复请求。
 */
export function loadProvinces(): Promise<ProvinceOption[]> {
  if (!provincesPromise) {
    provincesPromise = (async () => {
      const res = await fetch('/geo/china.json');
      if (!res.ok) throw new Error(`省份数据加载失败（HTTP ${res.status}）`);
      const geo = (await res.json()) as { features: ChinaGeoFeature[] };
      return geo.features
        .filter((f) => f.properties.level === 'province' && !!f.properties.name)
        .map((f) => ({
          code: String(f.properties.adcode),
          name: f.properties.name as string,
          center: f.properties.center ?? f.properties.centroid ?? null,
        }))
        .sort((a, b) => a.code.localeCompare(b.code));
    })();
  }
  return provincesPromise;
}

/** 便捷：按省码取省名（表单回显用） */
export async function provinceNameOf(code: string): Promise<string | undefined> {
  const list = await loadProvinces();
  return list.find((p) => p.code === code)?.name;
}
